import { logCustomEvent, changeUser, initialize ,logPurchase, openSession, isPushSupported, requestPushPermission, requestImmediateDataFlush } from "@braze/web-sdk";
import * as CONSTANT from "../constants";
import { toFixedNumber } from "../priceCalculation/helper";
import { getSelectedStore, getUser } from "./helperMethods";
import { PickUpMethods } from "./tracking";
import { IUserInfo } from "../models/brazePayloads.model";
import { IApplyOfferOnCart, IUpsellClickPayloadForBraze } from "../models/order.model";
import { orderMicroService } from "../services";
import { isUserAuthorizedForPush } from "../components/utils";
import branch from 'branch-sdk';

let brazeSessionReadyCallback: (() => void) | null = null;

/** Coordinator registers to run the single batched banner fetch after session open. */
export function setBrazeSessionReadyCallback(callback: () => void) {
  brazeSessionReadyCallback = callback;
}

export const brazeInititalize = () => {
  if (Boolean(process.env.REACT_APP_ENABLE_BRAZE_TRACKING)) {
    let sessionTimeout;
    initialize(process.env.REACT_APP_BRAZE_API_KEY, {
      baseUrl: process.env.REACT_APP_BRAZE_BASE_URL,
      enableLogging: Boolean(
        process.env.REACT_APP_BRAZE_ENABLE_CONSOLE_LOGGING
      ),
      serviceWorkerLocation: '/service-worker.js',
      allowUserSuppliedJavascript: true,
    });

    sessionTimeout && clearTimeout(sessionTimeout);
    sessionTimeout = setTimeout(() => {
      const apiKey = process.env.REACT_APP_BRAZE_API_KEY;
      localStorage.removeItem(`ab.storage.messagingSessionStart.${apiKey}`);
      // Clear the banner session so requestBannersRefresh always fetches
      // fresh banners from the backend on every page load.
      localStorage.removeItem("ab.storage.banners.sessionId");
      if (apiKey) {
        localStorage.removeItem(`ab.storage.banners.sessionId.${apiKey}`);
      }
      openSession();
      requestImmediateDataFlush();
      brazeSessionReadyCallback?.();
      console.log('Braze session cleared & refreshed.');
    }, 2000);

    // Register push capabilities after initialization
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log(
            'Service Worker registered with scope:',
            registration.scope
          );
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }
};
/**
 * Check if push notifications are supported and enabled
 * This combines Braze's isPushSupported with browser permission check
 */
export const checkPushStatus = () => {
    // First check if push is supported by the browser
    const pushSupported = isPushSupported();
    
    // Then check if permission is granted
    const isPushEnabled = pushSupported && Notification.permission === "granted";
    
    return {
      pushSupported,
      isPushEnabled
    };
  };
  
  /**
   * Show a soft push prompt to the user
   * Call this before showing the browser's native permission dialog
   */
  export const showSoftPushPrompt = (onUserResponse) => {

    return new Promise((resolve) => {
      const user = getUser();
      if (!isUserAuthorizedForPush(user?.email)) {
        console.log("User not authorized for push notifications:", user?.email);
        if (onUserResponse) onUserResponse(false);
        resolve(false);
        return;
      }
      // Create modal elements
      const modalOverlay = document.createElement('div');
      modalOverlay.className = 'modal-overlay';
      modalOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
      `;
  
      const modalContent = document.createElement('div');
      modalContent.className = 'modal-content';
      modalContent.style.cssText = `
        background-color: white;
        border-radius: 12px;
        padding: 24px;
        max-width: 400px;
        width: 90%;
        text-align: center;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      `;
  
      // Add notification icon
      const notificationIcon = document.createElement('div');
      notificationIcon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
      `;
      notificationIcon.style.cssText = `
        margin-bottom: 16px;
        color: #262626;
      `;
  
      // Add title and description
      const title = document.createElement('h2');
      title.textContent = 'Turn on Notifications';
      title.style.cssText = `
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 8px;
      `;
  
      const description = document.createElement('p');
      description.textContent = 'Never miss a tasty update! Get notified about exclusive deals and fresh offers.';
      description.style.cssText = `
        font-size: 14px;
        color: #737373;
        margin-bottom: 24px;
      `;
  
      // Add buttons
      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 12px;
      `;
  
      const allowButton = document.createElement('button');
      allowButton.textContent = 'Allow';
      allowButton.style.cssText = `
        background-color: #0956A8;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 12px;
        font-weight: 600;
        cursor: pointer;
      `;
  
      const notNowButton = document.createElement('button');
      notNowButton.textContent = 'Not Now';
      notNowButton.style.cssText = `
        background-color: transparent;
        color: #262626;
        border: none;
        padding: 12px;
        font-weight: 500;
        cursor: pointer;
      `;
  
      // Add event listeners
      allowButton.addEventListener('click', () => {
        // Remove modal
        document.body.removeChild(modalOverlay);
        localStorage.setItem('notifications', "false")
        
        if(/Edg/.test(navigator.userAgent)){
            Notification.requestPermission().then(permission => {
                if (permission === "granted") {
                  console.log("The user accepted to receive notifications");
                  if (onUserResponse) onUserResponse(permission);
                  resolve(permission);
                }
              });  
        }else{
            requestBrazePushPermission()
              .then(granted => {
                if (granted) {
                  console.log("Push permission granted after soft prompt");
                } else {
                  console.log("Push permission denied after soft prompt");
                }
                if (onUserResponse) onUserResponse(granted);
                resolve(granted);
              });
        }
      });
  
      notNowButton.addEventListener('click', () => {
        // Remove modal
        document.body.removeChild(modalOverlay);
        localStorage.setItem('notifications', "false")
        
        console.log("Soft push prompt declined");
        if (onUserResponse) onUserResponse(false);
        resolve(false);
      });
  
      // Assemble and append modal to body
      buttonContainer.appendChild(allowButton);
      buttonContainer.appendChild(notNowButton);
      
      modalContent.appendChild(notificationIcon);
      modalContent.appendChild(title);
      modalContent.appendChild(description);
      modalContent.appendChild(buttonContainer);
      
      modalOverlay.appendChild(modalContent);
      document.body.appendChild(modalOverlay);
    });
  };
  
  /**
   * Request push permission using Braze's API
   */
  export const requestBrazePushPermission = () => {
    return new Promise((resolve) => {
      if (!isPushSupported()) {
        console.log("Push notifications are not supported in this browser");
        resolve(false);
        return;
      }
  
      // Use Braze's push permission API
      // This method handles both requesting permission and registering the push token
      requestPushPermission((granted) => {
        if (granted) {
          console.log("Push permission granted");
          
          // Force data flush to ensure token is sent to Braze
          requestImmediateDataFlush();
        } else {
          console.log("Push permission denied");
        }
        resolve(granted);
      });
    });
  };
  

export const brazeSetUser = (userId: string) => {
    if (!Boolean(process.env.REACT_APP_ENABLE_BRAZE_TRACKING)) return;
    changeUser(userId);
}
    
export const brazeLogCustomEvent = async (
  eventName,
  eventProperties = null
) => {
    if (!Boolean(process.env.REACT_APP_ENABLE_BRAZE_TRACKING)) return;
    if (eventProperties) logCustomEvent(eventName, eventProperties);
    else logCustomEvent(eventName);
};


const getItemImageURL = (itemId: number) => `${CONSTANT.REACT_APP_CLOUD_FRONT}/Items/${itemId}/item-${itemId}.webp`
const getMappedObject = (item: any, isOrderCancelledEvent: boolean, isCombo: boolean = false) => {
    return {
        itemName: item.name || item.modifier_name,
        ...(!isCombo && { price: item.price }),
        ...(item.item_size && { size: item.item_size }),
        ...(!isCombo && !isOrderCancelledEvent && {
            image: getItemImageURL(item.item_id),
        }),
        quantity: item.quantity,
        ...(item.modifiers && {
        modsAdded: item?.modifiers?.map((modifier) => modifier.modifier_name),
        }),
    };
};

const getComboMappedItems = (comboItem: any, isOrderCancelledEvent: boolean) => {
    const comboMappedObject = {
        itemName: comboItem.name,
        price: comboItem.price,
        size: comboItem.item_size,
        ...(!isOrderCancelledEvent && {
            image: getItemImageURL(comboItem.item_id),
        }),
        quantity: comboItem.quantity,
        items: comboItem.modifiers.map(modifier_item => getMappedObject(modifier_item, isOrderCancelledEvent, true))
    }
    return comboMappedObject;
}

const getMappedItems = (items: any[], isOrderCancelledEvent: boolean = false) => {
    if (!items?.length) return [];

    const mappedItems = [];

    items?.forEach(item => {
        if (
            [CONSTANT.TRY_TO_COMBO_ITEM, CONSTANT.KIDS_COMBO_ITEM].includes(item.combo_type) ||
            item.isKidsCombo ||
            item.isTry2Combo ||
            item.is_kids_combo ||
            item.is_try_2_combo
            )
            mappedItems.push(getComboMappedItems(item, isOrderCancelledEvent))
        else 
            mappedItems.push(getMappedObject(item, isOrderCancelledEvent))
    });
    return mappedItems;
}

const getAccountType = (userType: string) =>
  userType === CONSTANT.GUEST_USER
    ? CONSTANT.GUEST_USER
    : CONSTANT.LOYALTY_USER;

const getIsLoyaltyRedeemed = (orderItems) => {
    return !!orderItems?.find(item => item.discount);
}

export type BrazeLoyaltyLoginMethod = "Email" | "Gmail" | "Apple" | "Auto";

export type LoginPayloadSource =
  | 'login_page'
  | 'signup_page'
  | 'guest_checkout_modal'
  | 'auto_after_order';

/** Shared login payload for Braze and Pulse. */
export const buildLoginPayload = (
  userData: any,
  isItMobile: boolean,
  loginMethod: BrazeLoyaltyLoginMethod | 'Facebook',
  loginSource: LoginPayloadSource,
) => ({
  CzId: userData?.id,
  email: userData?.email,
  orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB : CONSTANT.SOURCE_WEB,
  loginMethod,
  loginSource,
});

export const brazeLogCustomEventLoyaltySignIn = (
  isItMobile: boolean,
  loginMethod: BrazeLoyaltyLoginMethod,
  loginSource: LoginPayloadSource = 'login_page',
) => {
    const user = getUser()
    const loyaltySignInPayload = buildLoginPayload(
      user,
      isItMobile,
      loginMethod,
      loginSource,
    );
    brazeLogCustomEvent(CONSTANT.LOYALTY_SIGN_IN, loyaltySignInPayload)
}

/** Shared ORDER_PLACED payload for Braze, Branch, and Pulse. */
export const buildOrderPlacedPayload = (
  orderId: string | number,
  order: any,
  isItMobile: boolean,
) => {
  const promiseTime = order?.promise_time?.split(' ');
  const user = getUser();
  const categoriesOrdered = order?.items?.map((item) => item.category_name);
  const pickUpMethod = PickUpMethods[order?.order_types?.id];
  return {
    firstName: order?.customer?.first_name,
    lastName: order?.customer?.last_name,
    orderId,
    contact: order?.customer?.phone,
    email: order?.customer?.email,
    storeId: order?.location?.id,
    storeName: order?.location?.name,
    brinkId: order?.location?.brink_id,
    orderType: order?.order_types?.name,
    orderDate: promiseTime?.[0],
    orderTime: promiseTime?.[1],
    zipCode: order?.customer?.address?.zipcode || order?.location?.zip,
    orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB : CONSTANT.SOURCE_WEB,
    accountType: getAccountType(user?.type),
    isTipGiven: Boolean(order?.tip),
    isLoyaltyRedeemed: getIsLoyaltyRedeemed(order?.items),
    categoriesOrdered,
    items: getMappedItems(order?.items),
    subtotal: order?.sub_total,
    tax: order?.tax,
    discount: order?.discount,
    tipAmount: order?.tip,
    ...(pickUpMethod === CONSTANT.DELIVERY && {
      deliveryFee: CONSTANT.DELIVERY_FEE,
      serviceFee: CONSTANT.SERVICE_FEE,
    }),
    total: order?.total_price,
    pickUpMethod,
  };
};

export const brazeLogCustomEventOrderPlaced = async (orderId, order: any, isItMobile: boolean) => {
  try {
    const orderPlacedPayload = buildOrderPlacedPayload(orderId, order, isItMobile);
    const { pickUpMethod, ...brazePayload } = orderPlacedPayload;
    await brazeLogCustomEvent(CONSTANT.ORDER_PLACED, brazePayload);
    await trackBranchOrderPlaced(brazePayload, pickUpMethod, isItMobile);
  } catch (error) {
    console.log(error)
  }
}

export const brazeLogCustomEventFavoriteOrder = (order: any,isItMobile:boolean) => {

    const orderPlacedPayload = {
        orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB,
        favoriteOrderName: order.favorite_order_name || order.order_name,
        categoriesOrdered: order.categories_ordered,
        items: getMappedItems(order?.items),
    }


    brazeLogCustomEvent(CONSTANT.SAVE_FAVORITE_ORDER, orderPlacedPayload)
}
export const brazeLogCustomEventReOrder = (order: any, isFavorite:boolean=false,isItMobile:boolean) => {
    
    const orderPlacedPayload = {
        orderPlatform:isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB,
        categoriesOrdered: order.categories_ordered,
        items: getMappedItems(order?.items),
        ...(order.favorite_order_name && {favoriteOrderName: order.favorite_order_name})
        
    }
    brazeLogCustomEvent(isFavorite ? CONSTANT.REORDER_CLICKED_FROM_FAVORITE_ORDERS : CONSTANT.REORDER_CLICKED_FROM_RECENT_ORDERS, orderPlacedPayload)
}



export const brazeLogCustomEventOrderCancelled = (order: any, isItMobile: boolean) => {
    const promiseTime = order?.promise_time?.split(" ")
    const user = getUser()
    const categoriesOrdered = order?.items?.map(item => item.category_name)
    const pickUpMethod = PickUpMethods[order?.order_types?.id]
    const orderCancelledPayload = {
        firstName: order?.customer?.first_name, 
        lastName: order?.customer?.last_name, 
        contact: order?.customer?.phone, 
        email: order?.customer?.email, 
        storeId: order?.store_details?.id, 
        storeName: order?.store_details?.name, 
        orderType: order?.order_types?.name, 
        orderDate: promiseTime?.[0],
        orderTime: promiseTime?.[1],
        zipCode: order?.customer?.zipcode || order?.store_details?.zip, 
        orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB, 
        accountType: getAccountType(user?.type), 
        isTipGiven: Boolean(order?.tip),
        isLoyaltyRedeemed: getIsLoyaltyRedeemed(order?.items),
        categoriesOrdered: categoriesOrdered,
        items: getMappedItems(order?.items, true),
        subtotal: order?.sub_total,
        tax: order?.tax,
        discount: order?.discount,
        tipAmount: order?.tip,
        ...(pickUpMethod === CONSTANT.DELIVERY && {
            deliveryFee: CONSTANT.DELIVERY_FEE,
            serviceFee: CONSTANT.SERVICE_FEE,
        }),
        total: order?.total_price,
    }
    brazeLogCustomEvent(CONSTANT.ORDER_CANCELLED, orderCancelledPayload)
}

export interface CheckoutStartedOrderAmount {
    discount: number;
    tax: number;
    subTotal: number;
    total: number;
}

/** Shared CHECKOUT_STARTED payload for Braze, Branch, and Pulse. */
export const buildCheckoutStartedPayload = (
    cart: any,
    orderAmount: CheckoutStartedOrderAmount,
    isItMobile: boolean,
    slotDetails: any,
) => {
    const user = getUser();
    const selectedStore = getSelectedStore();
    const categoriesOrdered = cart?.items?.map((item) => item.category_name);
    return {
        firstName: user.first_name,
        lastName: user?.last_name,
        contact: user?.phone,
        email: user?.email,
        storeId: selectedStore.id,
        storeName: selectedStore?.name,
        brinkId: selectedStore?.brink_id,
        orderType: slotDetails?.pickupMethod || cart?.orderType,
        cartId: cart?.orderId,
        isAsapOrder: slotDetails?.isAsapOrder,
        orderDate: slotDetails?.date,
        orderTime: slotDetails?.isAsapOrder
            ? slotDetails?.asapMinutes
            : slotDetails?.time,
        zipCode:
            cart?.orderType === CONSTANT.DELIVERY_ORDER_TYPE
                ? cart?.orderDetails?.delivery_address?.zipcode
                : selectedStore.zip,
        orderPlatform: isItMobile
            ? CONSTANT.SOURCE_MOBILE_WEB
            : CONSTANT.SOURCE_WEB,
        discount: orderAmount?.discount,
        DiscountApplied: Boolean(orderAmount?.discount),
        offerName: cart?.offerName,
        rootOfferId: cart?.root_offer_id,
        userOfferId: cart?.user_offer_id,
        accountType: getAccountType(user?.type),
        isLoyaltyRedeemed: getIsLoyaltyRedeemed(cart?.items),
        categoriesOrdered,
        items: getMappedItems(cart?.items),
        subtotal: orderAmount?.subTotal,
        tax: orderAmount?.tax,
        ...(cart?.orderType === CONSTANT.DELIVERY_ORDER_TYPE && {
            deliveryFee: CONSTANT.DELIVERY_FEE,
            serviceFee: CONSTANT.SERVICE_FEE,
        }),
        total: orderAmount?.total,
    };
};

export const brazeLogCustomEventCheckoutStarted = async (
    cart: any,
    orderAmount: CheckoutStartedOrderAmount,
    isItMobile: boolean,
    slotDetails: any,
) => {
    const checkoutStartedPayload = buildCheckoutStartedPayload(
        cart,
        orderAmount,
        isItMobile,
        slotDetails
    );
    await brazeLogCustomEvent(CONSTANT.CHECKOUT_STARTED, checkoutStartedPayload);
    await trackBranchCheckoutStarted(checkoutStartedPayload, isItMobile);
};

const getGuestCheckoutClickedPayload = (
    cart: any,
    subTotal: number,
    isItMobile: boolean,
) => {
    const user = getUser();
    const selectedStore = getSelectedStore();
    const categoriesOrdered = cart?.items?.map((item) => item.category_name);
    const clickedAt = new Date();
    return {
        firstName: user?.first_name,
        lastName: user?.last_name,
        contact: user?.phone,
        email: user?.email,
        storeId: selectedStore?.id,
        storeName: selectedStore?.name,
        orderType: cart?.orderType,
        cartId: cart?.orderId,
        zipCode:
            cart?.orderType === CONSTANT.DELIVERY_ORDER_TYPE
                ? cart?.orderDetails?.delivery_address?.zipcode
                : selectedStore?.zip,
        orderPlatform: isItMobile
            ? CONSTANT.SOURCE_MOBILE_WEB
            : CONSTANT.SOURCE_WEB,
        accountType: CONSTANT.GUEST_USER,
        categoriesOrdered,
        items: getMappedItems(cart?.items),
        subtotal: subTotal,
        clickedAt: clickedAt.toISOString(),
        clickedDate: clickedAt.toLocaleDateString("en-US"),
        clickedTime: clickedAt.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }),
    };
};

export const brazeLogCustomEventGuestCheckoutClicked = (
    cart: any,
    subTotal: number,
    isItMobile: boolean,
) => {
    brazeLogCustomEvent(
        CONSTANT.GUEST_CHECKOUT_CLICKED,
        getGuestCheckoutClickedPayload(cart, subTotal, isItMobile),
    );
};

/** Same payload as GUEST_CHECKOUT_CLICKED; used by checkout login modal actions. */
export const brazeLogCustomEventCheckoutLoginAction = (
    eventName: string,
    cart: any,
    subTotal: number,
    isItMobile: boolean,
) => {
    brazeLogCustomEvent(
        eventName,
        getGuestCheckoutClickedPayload(cart, subTotal, isItMobile),
    );
};

/** Shared ADDED_TO_CART payload for Braze, Branch, and Pulse. */
export const buildAddedToCartPayload = (
  cart: any,
  cartId: any,
  itemAdded: any,
  isItMobile: boolean,
  addedFromUpsellSection = false,
) => {
  const user = getUser();
  const selectedStore = getSelectedStore();
  return {
    storeId: selectedStore.id,
    storeName: selectedStore?.name,
    orderType: cart?.orderType,
    cartId: cart?.orderId || cartId,
    zipCode:
      cart?.orderType === CONSTANT.DELIVERY_ORDER_TYPE
        ? cart?.orderDetails?.delivery_address?.zipcode
        : selectedStore.zip,
    orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB : CONSTANT.SOURCE_WEB,
    accountType: getAccountType(user?.type),
    items: getMappedItems(itemAdded),
    total: toFixedNumber(itemAdded[0]?.display_price * itemAdded[0]?.quantity),
    addedFromUpsellSection: Boolean(addedFromUpsellSection),
    selectedStore,
    itemAdded,
  };
};

export const brazeLogCustomEventAddedToCart = async (
  cart: any,
  cartId: any,
  itemAdded: any,
  isItMobile: boolean,
  addedFromUpsellSection = false,
) => {
    const addedToCartPayload = buildAddedToCartPayload(
      cart,
      cartId,
      itemAdded,
      isItMobile,
      addedFromUpsellSection,
    );
    const { selectedStore, itemAdded: itemsForBranch, ...brazePayload } =
      addedToCartPayload;
    await brazeLogCustomEvent(CONSTANT.ADDED_TO_CART, brazePayload);
    await trackBranchAddToCart(
      brazePayload,
      selectedStore,
      itemsForBranch,
      isItMobile,
    );
}

export const brazeLogCustomEventViewPage = (pageName: string, isItMobile: boolean) => {
    const user = getUser()
    const viewPagePayload = {
        pageName,
        orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB, 
        accountType: getAccountType(user?.type), 
    }
    brazeLogCustomEvent(CONSTANT.VIEW_PAGE, viewPagePayload);
}

export type BrazeLoyaltySignupMethod = "Email" | "Gmail" | "Apple";

export const brazeLogCustomEventLoyaltyAccountCreated = (
  userInfo,
  signupMethod: BrazeLoyaltySignupMethod,
) => {
    let dateOfBirth = "";
    if (userInfo.birthday) {
        const splitBirthday = userInfo.birthday.split("-");
        dateOfBirth = `${splitBirthday[1]}/${splitBirthday[2]}/${splitBirthday[0]}`;
    }
    let favoriteLocations = [];
    if (userInfo?.favorite_location)
        favoriteLocations = userInfo?.favorite_location?.map(location => location.label);
    const loyaltyAccountCreatedPayload = {
        firstName: userInfo.first_name, 
        lastName: userInfo.last_name, 
        contact: userInfo.phone || "", 
        email: userInfo.email, 
        dateOfBirth, 
        favoriteLocations,
        signupMethod,
    }      
    brazeLogCustomEvent(CONSTANT.LOYALTY_ACCOUNT_CREATED, loyaltyAccountCreatedPayload)
}

export const brazeLogCustomEventAddToFavorites = (item: any, isItMobile: boolean) => {
    const user = getUser();
    const addToFavoritesPayload ={
        itemName: item.name,
        itemTag: item.tags.map(tag => tag.name),
        itemDescription: item.description,
        itemCategory: item.category_name || "",
        price: item.price,
        image: getItemImageURL(item.id),
        orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB, 
        accountType: getAccountType(user?.type), 
    }
    brazeLogCustomEvent(CONSTANT.ADD_TO_FAVORITES, addToFavoritesPayload);
}

export const brazeLogCustomEventFormSubmission = (formName: string, isItMobile: boolean, formFields: any) => {
    const user = getUser();
    const formSubmissionPayload = {
        formName, 
        orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB, 
        accountType: getAccountType(user?.type), 
        data: {
            ...formFields,
        }
    }
    brazeLogCustomEvent(CONSTANT.FORM_SUBMISSION, formSubmissionPayload);
}

const getMappedPurchaseItems = (data: any, isItMobile?:boolean) => {
    if (!data?.items?.length) return [];
    data?.items?.forEach((item: any) => {
      if (
        [CONSTANT.TRY_TO_COMBO_ITEM,CONSTANT.KIDS_COMBO_ITEM].includes(item?.combo_type) ||
        item?.isKidsCombo ||
        item?.isTry2Combo ||
        item?.is_kids_combo ||
        item?.is_try_2_combo
      )
        getComboMappedPurchaseItems(item, data,isItMobile);
      else getMappedPurchaseObject(item, data,null,isItMobile);
    });
  };
  
  const getMappedPurchaseObject =  (
    item: any,
    data?: any,
    from?: string,
    isItMobile?:boolean
  ) => {
    const promiseTime = data?.promise_time?.split(" ")

    const brazeObj = {
        orderId: data?.id,
      PosId: item?.brink_id || null,
      itemName: item?.name || item?.modifier_name || '',
      category: item?.category_name || '',
      size: item?.size || '',
      orderDate: promiseTime?.[0],
      orderTime: promiseTime?.[1],
      orderType: data?.order_types?.name,
      grossPrice: (item?.gross_price || 0.0).toFixed(2),
      price:item?.quantity * item?.price,
      storeId: data?.location?.id,
      storeName: data?.location?.name,
      image: getItemImageURL(item?.item_id),
      orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB, 
        modsAdded: item?.modifiers_added?.length ? item?.modifiers_added : [],
    };
    if (from) {
      return brazeObj;
    }
    logPurchase(
      item?.name,
      (item?.gross_price *  item?.quantity),
      'USD',
      item?.quantity,
      brazeObj,
    );
  };
  
  const getComboMappedPurchaseItems =  (comboItem: any, data: any,isItMobile?:boolean) => {
    const promiseTime = data?.promise_time?.split(" ")
   
    const comboMappedObject = {
        orderId: data?.id,
      PosId: comboItem?.brink_id || null,
      itemName: comboItem?.name || '',
      category: comboItem?.category_name || '',
      size: comboItem?.size || '',
      orderDate: promiseTime?.[0],
      orderTime: promiseTime?.[1],
      orderType: data?.order_types?.name,
      grossPrice:(comboItem?.quantity* comboItem?.gross_price || 0.0).toFixed(2),
      price:  comboItem?.price,
      storeId: data?.location?.id,
      storeName: data?.location?.name,
      image: getItemImageURL(comboItem?.item_id),
      itemId: comboItem?.item_id,
      orderPlatform: isItMobile ? CONSTANT.SOURCE_MOBILE_WEB: CONSTANT.SOURCE_WEB, 

      modsAdded: comboItem?.modifiers_added?.length ? comboItem?.modifiers_added : [],
      items: comboItem?.modifiers?.map(modifierItem => {
         return getMappedPurchaseObject(modifierItem, data, 'combo',isItMobile);
        }) || [],
    };


    logPurchase(
        comboItem?.name,
        (comboItem?.gross_price * comboItem?.quantity),
        'USD',
        comboItem?.quantity,
        comboMappedObject,
      );
  
}
  
export const brazeLogOrderPlace = (orderedItems: any, isItMobile?: boolean) => { 
    getMappedPurchaseItems(orderedItems,isItMobile)
}
  
export const brazeLogEventSubscribedSMSMarketing = (userInfo: IUserInfo) => {
    const smsMarketingPayload = 
    {
        CzId: userInfo?.id,
        firstName: userInfo?.first_name,
        lastName: userInfo?.last_name, 
        contact: userInfo?.phone,
        email: userInfo?.email 
    }
    brazeLogCustomEvent(CONSTANT.SUBSCRIBE_TO_SMS_MARKETING, smsMarketingPayload);
}

export const brazeLogEventClaimPromoCode = (promo_code: string, userId: number, cartId: number) => {
    const claimPromoPayload = 
    {
        customer_id: userId,
        cart_id: cartId,
        promo_code: promo_code,
        source: CONSTANT.SOURCE_WEB
    }
    brazeLogCustomEvent(CONSTANT.CLAIM_PROMO_CODE, claimPromoPayload);
}

export const brazeLogEventOfferAdded = (offerData: IApplyOfferOnCart, userId: number, cartId: number) => {
    const offerAddedPayload = 
    {
        root_offer_id: offerData?.root_offer_id,
        user_offer_id: offerData?.user_offer_id,
        eligible_item_offer_id: offerData?.eligible_item_offer_id,
        customer_id: userId,
        cart_id: cartId,
        source: CONSTANT.SOURCE_WEB
    }
    brazeLogCustomEvent(CONSTANT.OFFER_ADDED_CART, offerAddedPayload);
}

export const brazeLogEventOnUpsellClick = (payload: IUpsellClickPayloadForBraze) => {
    brazeLogCustomEvent(CONSTANT.UPSELL_CLICK, payload);
}

/**
 * Create Branch Web SDK content items from cart items
 * Handles both simple items and combo items with nested structure
 * @param itemAdded - Array of cart items added
 * @returns Array of Branch content items
 */
const createBranchContentItemsForWeb = (itemAdded: any[]): any[] => {
  const contentItems: any[] = [];
  itemAdded.forEach((item, index) => {
    // Check if this is a combo item (Try2Combo, Kids Combo, etc.)
    const isComboItem =
      item.is_try_2_combo ||
      item.is_kids_combo ||
      [CONSTANT.TRY_TO_COMBO_ITEM, CONSTANT.KIDS_COMBO_ITEM].includes(
        item.item_type
      );

    if (isComboItem && item.modifiers && Array.isArray(item.modifiers)) {
      // Handle combo items - create content items for each sub-item
      const comboItems: any[] = [];
      const comboModsAdded: string[] = [];

      item.modifiers.forEach((modifier, modifierIndex) => {
        if (modifier.is_item) {
          // This is a main item in the combo (sandwich, salad, etc.)
          // Get modifiers for this sub-item (where is_item: false)
          const subItemMods = modifier.modifiers
            ? modifier.modifiers
                .filter((mod) => !mod.is_item)
                .map((mod) => mod.modifier_name)
                .filter((name) => name)
            : [];

          const contentItem = {
            $item_id: String(modifier.modifier_id || ""),
            $quantity: Number(modifier.quantity || 1),
            $product_name: modifier.modifier_name || item.name,
            $product_category:
              modifier.category_name || item.category_name || "Combo",
            $product_variant: modifier.modifier_size || item.item_size || "",
            modsAdded: subItemMods,
          };
          comboItems.push(contentItem);
        } else {
          // This is a modifier for the combo itself (is_item: false)
          if (modifier.modifier_name) {
            comboModsAdded.push(modifier.modifier_name);
          }
        }
      });

      // Add the combo container item with comboItems and modsAdded
      const comboContainerItem = {
        $item_id: String(item.item_id || ""),
        $product_name: item.name,
        $price: Number(parseFloat(item.display_price || 0).toFixed(2)),
        $quantity: Number(item.quantity || 1),
        $product_category_name: "test",
        $product_variant: item.item_size || "",
        comboItems: comboItems,
        currency: "USD",
        modsAdded: comboModsAdded,
      };
      contentItems.push(comboContainerItem);
    } else {
      // Handle simple items
      // Get modifiers for single item (where is_item: false)
      const itemModsAdded = item.modifiers
        ? item.modifiers
            .filter((mod) => !mod.is_item)
            .map((mod) => mod.modifier_name)
            .filter((name) => name)
        : [];
      const contentItem = {
        $product_id: String(item.item_id || ""),
        $product_name: item.name,
        $price: Number(parseFloat(item.display_price || 0).toFixed(2)),
        $quantity: Number(item.quantity || 1),
        $product_category_name: item.category_name || "Food",
        $product_variant: item.item_size || "",
        modsAdded: itemModsAdded,
      };
      contentItems.push(contentItem);
    }
  });

  return contentItems;
};

/**
 * Track Add to Cart Event in Branch Web SDK V2
 * Uses proper Branch Web SDK format with content_items array
 * @param brazePayload - The exact payload sent to Braze SDK
 * @param selectedStore - Store information
 * @param itemAdded - Array of cart items that were added
 */
export const trackBranchAddToCart = async (brazePayload: any, selectedStore: any, itemAdded: any[], isItMobile: boolean) => {
  try {
    console.log('🚀 [Branch Web V2] trackBranchAddToCart called with payload:', {
      brazePayload,
      selectedStore,
      itemAdded
    });
    
    // Check if branch is available
    if (typeof branch === 'undefined') {
      console.error('❌ [Branch Web V2] Branch SDK not loaded');
      return;
    }

    // Validate required data
    if (!itemAdded || !Array.isArray(itemAdded) || itemAdded.length === 0) {
      console.warn('⚠️ [Branch Web V2] No items provided for AddToCart event');
      return;
    }

    const currentItemCartId = itemAdded[0]?.id;

    // Create Branch content items from your cart data
    const content_items = createBranchContentItemsForWeb(itemAdded);
    // Define event data following Branch Web SDK format
    const event_and_custom_data = {
      "transaction_id": currentItemCartId ? String(currentItemCartId) : undefined,
      "currency": "USD",
      "revenue": Number(parseFloat(String(brazePayload.total || 0)).toFixed(2)),
      "affiliation": selectedStore?.brink_id ? `${selectedStore.brink_id}-${brazePayload.storeName}` : brazePayload.storeName || undefined,
      "customData": {
        payload: JSON.stringify(brazePayload),
        eventSource: 'web',
        platform: isItMobile ? "MobileWeb" : "web",
      }
    };

    // Log the event using Branch Web SDK
    branch.logEvent(
      "ADD_TO_CART",
      event_and_custom_data,
      content_items,
      null, // No customer alias needed
      function(err) { 
        if (err) {
          console.error('❌ [Branch Web V2] Error in logEvent callback:', err);
          console.error('❌ [Branch Web V2] Full error object:', JSON.stringify(err, null, 2));
        } else {
          console.log('✅ [Branch Web V2] ADD_TO_CART event logged successfully');
          console.log('📊 [Branch Web V2] Event should appear in Branch dashboard within 5-10 minutes');
          console.log('🔗 [Branch Web V2] Check Branch dashboard at: https://dashboard.branch.io');
          console.log(`📈 [Branch Web V2] Tracked ${content_items.length} content items for ${itemAdded.length} cart items`);
        }
      }
    );

  } catch (error) {
    console.error('❌ [Branch Web V2] Error logging AddToCart event:', error);
    console.error('❌ [Branch Web V2] Error details:', JSON.stringify(error, null, 2));
    console.error('❌ [Branch Web V2] Item data that caused error:', JSON.stringify(itemAdded, null, 2));
  }
};

export const trackBranchOrderPlaced = async (brazePayload: any, pickUpMethod, isItMobile: boolean) => {
  try {
    console.log('🚀 [Branch Web] trackBranchOrderPlaced called with payload:', brazePayload, pickUpMethod);
    
    // Check if branch is available
    if (typeof branch === 'undefined') {
      console.error('❌ [Branch Web] Branch SDK not loaded');
      return;
    }

    let isPickup = brazePayload.orderType !== CONSTANT.DELIVERY_ORDER_TYPE;
    if(pickUpMethod === CONSTANT.DELIVERY){
      isPickup = false;
    }
    const deliveryFee = parseFloat(brazePayload.deliveryFee || 0);
    const serviceFee = parseFloat(brazePayload.serviceFee || 0);
    const shipping = isPickup ? 0 : deliveryFee + serviceFee;
    // Define event data
    var event_and_custom_data = {
      "transaction_id": brazePayload.orderId ? String(brazePayload.orderId) : undefined,
      "currency": "USD",
      "revenue": brazePayload?.subtotal || 0,
      tax: brazePayload?.tax || 0,
      shipping,
      // coupon: brazePayload.coupon || undefined,
      "affiliation": `${brazePayload?.brinkId}-${brazePayload?.storeName}` || undefined,
      "custom_data": {
            payload: brazePayload,
            eventSource: 'web',
            platform: isItMobile ? "MobileWeb" : "web",
          }
    };
  branch.logEvent(
    "PURCHASE",
    event_and_custom_data,
    function(err) { 
      if (err) {
        console.error('❌ [Branch Web] Error in logEvent callback:', err);
        console.error('❌ [Branch Web] Full error object:', JSON.stringify(err, null, 2));
      } else {
        console.log('✅ [Branch Web] PURCHASE event logged successfully');
        console.log('📊 [Branch Web] Event should appear in Branch dashboard within 5-10 minutes');
        console.log('🔗 [Branch Web] Check Branch dashboard at: https://dashboard.branch.io');
      }
    }
  );
  } catch (error) {
    console.error('❌ [Branch Web] Error logging InitiatePurchase event:', error);
    console.error('❌ [Branch Web] Error details:', JSON.stringify(error, null, 2));
  }
};

/**
 * Track Checkout Started Event in Branch for Web
 * Uses Branch Web SDK track method with InitiatePurchase event
 * @param brazePayload - The exact payload sent to Braze SDK
 */
export const trackBranchCheckoutStarted = async (brazePayload: any, isItMobile:boolean) => {
  try {
    console.log('🚀 [Branch Web] trackBranchCheckoutStarted called with payload:', brazePayload);
    
    // Check if branch is available
    if (typeof branch === 'undefined') {
      console.error('❌ [Branch Web] Branch SDK not loaded');
      return;
    }

    const isPickup = brazePayload.orderType !== CONSTANT.DELIVERY_ORDER_TYPE;
    const deliveryFee = parseFloat(brazePayload.deliveryFee || 0);
    const serviceFee = parseFloat(brazePayload.serviceFee || 0);
    const shipping = isPickup ? 0 : deliveryFee + serviceFee;
    // Define event data
    var event_and_custom_data = {
      "transaction_id": brazePayload.cartId ? String(brazePayload.cartId) : undefined,
      "currency": "USD",
      "revenue": brazePayload.total || 0,
      shipping,
      "affiliation": `${brazePayload?.brinkId}-${brazePayload?.storeName}` || undefined,
      "custom_data": {
            payload: brazePayload,
            eventSource: 'web',
            platform: isItMobile ? "MobileWeb" : "web",
          }
    };
  branch.logEvent(
    "INITIATE_PURCHASE",
    event_and_custom_data,
    function(err) { 
      if (err) {
        console.error('❌ [Branch Web] Error in logEvent callback:', err);
        console.error('❌ [Branch Web] Full error object:', JSON.stringify(err, null, 2));
      } else {
        console.log('✅ [Branch Web] PURCHASE event logged successfully');
        console.log('📊 [Branch Web] Event should appear in Branch dashboard within 5-10 minutes');
        console.log('🔗 [Branch Web] Check Branch dashboard at: https://dashboard.branch.io');
      }
    }
  );
  } catch (error) {
    console.error('❌ [Branch Web] Error logging InitiatePurchase event:', error);
    console.error('❌ [Branch Web] Error details:', JSON.stringify(error, null, 2));
  }
};